import { supabase } from '../supabase/client';

export interface WechselEintrag {
  id: string; typ: string; objekt_id: string; einheit_id: string | null;
  alt_ref_id: string; neu_ref_id: string | null;
  von_pfad: string | null; nach_pfad: string | null;
  faellig_am: string; status: 'geplant' | 'erledigt' | 'uebersprungen' | 'fehler' | 'storniert';
  versuche: number; fehler: string | null; ausgefuehrt_am: string | null; created_at: string;
}

export interface VormieterInfo { id: string; vorname: string | null; nachname: string | null; firma_name: string | null; mietende: string | null }

export function mieterAnzeigeName(m: { vorname?: string | null; nachname?: string | null; firma_name?: string | null }): string {
  return (m.firma_name || '').trim() || [m.nachname, m.vorname].filter(Boolean).join(', ') || 'Unbenannt';
}

// Aktive Mieter einer Einheit (Vormieter-Erkennung fuers Wechsel-Panel).
export async function ladeAktiveMieterDerEinheit(einheitId: string): Promise<VormieterInfo[]> {
  const { data } = await supabase.from('mieter')
    .select('id, vorname, nachname, firma_name, mietende, status')
    .eq('einheit_id', einheitId);
  return ((data || []) as (VormieterInfo & { status: string | null })[])
    .filter((m) => (m.status || '').toLowerCase() === 'aktiv');
}

// Plant je Vormieter einen Ordner-Wechsel (faellig_am = Mietbeginn des Nachfolgers).
export async function planeMieterWechsel(input: {
  objektId: string; einheitId: string | null; altRefIds: string[]; neuRefId: string;
  faelligAm: string; angelegtVon: string | null;
}): Promise<string[]> {
  const rows = input.altRefIds.map((alt) => ({
    typ: 'mieter', objekt_id: input.objektId, einheit_id: input.einheitId,
    alt_ref_id: alt, neu_ref_id: input.neuRefId, faellig_am: input.faelligAm, angelegt_von: input.angelegtVon,
  }));
  const { data, error } = await supabase.from('ordner_wechsel').insert(rows).select('id');
  if (error) throw new Error(error.message);
  return ((data || []) as { id: string }[]).map((r) => r.id);
}

export async function fuehreWechselAus(wechselIds: string[]): Promise<void> {
  const { error } = await supabase.functions.invoke('gdrive', { body: { action: 'wechsel-ausfuehren', wechselIds } });
  if (error) throw new Error(error.message);
}

export async function legeMieterOrdnerAn(mieterId: string): Promise<void> {
  const { error } = await supabase.functions.invoke('gdrive', { body: { action: 'mieter-ordner-anlegen', mieterId } });
  if (error) throw new Error(error.message);
}

// Setzt einen uebersprungenen/fehlgeschlagenen Wechsel zurueck auf 'geplant'
// (Zaehler+Fehler geleert), damit er erneut ausgefuehrt werden kann.
export async function planeWechselErneut(wechselId: string): Promise<void> {
  const { error } = await supabase.from('ordner_wechsel')
    .update({ status: 'geplant', versuche: 0, fehler: null, updated_at: new Date().toISOString() })
    .eq('id', wechselId);
  if (error) throw new Error(error.message);
}

export async function macheWechselRueckgaengig(wechselId: string): Promise<void> {
  const { error } = await supabase.functions.invoke('gdrive', { body: { action: 'wechsel-undo', wechselId } });
  if (error) throw new Error(error.message);
}

export async function ladeWechselProtokoll(limit = 50): Promise<WechselEintrag[]> {
  const { data } = await supabase.from('ordner_wechsel')
    .select('*').order('created_at', { ascending: false }).limit(limit);
  return (data || []) as WechselEintrag[];
}

export function heuteIso(): string { return new Date().toISOString().slice(0, 10); }

export interface ObjektEigentuemerInfo { eigentuemer_id: string; vorname: string | null; nachname: string | null; firma_name: string | null }

// Aktive Objekt-Eigentuemer (vertrag_bis offen) — Vorbesitzer-Erkennung fuers Wechsel-Panel.
export async function ladeAktiveObjektEigentuemer(objektId: string): Promise<ObjektEigentuemerInfo[]> {
  const { data } = await supabase.from('objekt_eigentuemer')
    .select('eigentuemer_id, vertrag_bis, eigentuemer:eigentuemer_id (vorname, nachname, firma_name)')
    .eq('objekt_id', objektId).is('vertrag_bis', null);
  return ((data || []) as any[]).map((r) => ({
    eigentuemer_id: r.eigentuemer_id,
    vorname: r.eigentuemer?.vorname ?? null,
    nachname: r.eigentuemer?.nachname ?? null,
    firma_name: r.eigentuemer?.firma_name ?? null,
  }));
}

export async function planeEigentuemerWechsel(input: {
  objektId: string; altRefIds: string[]; neuRefId: string; faelligAm: string; angelegtVon: string | null;
}): Promise<string[]> {
  const rows = input.altRefIds.map((alt) => ({
    typ: 'eigentuemer_objekt', objekt_id: input.objektId, einheit_id: null,
    alt_ref_id: alt, neu_ref_id: input.neuRefId, faellig_am: input.faelligAm, angelegt_von: input.angelegtVon,
  }));
  const { data, error } = await supabase.from('ordner_wechsel').insert(rows).select('id');
  if (error) throw new Error(error.message);
  return ((data || []) as { id: string }[]).map((r) => r.id);
}

// Eigentuemerwechsel je Einheit (WE/SEV): Ordner wandert, vertrag_bis pflegt die Ausfuehrung.
export async function planeEigentuemerEinheitWechsel(input: {
  objektId: string; einheitId: string; altRefId: string; neuRefId: string; faelligAm: string; angelegtVon: string | null;
}): Promise<string> {
  const { data, error } = await supabase.from('ordner_wechsel').insert({
    typ: 'eigentuemer_einheit', objekt_id: input.objektId, einheit_id: input.einheitId,
    alt_ref_id: input.altRefId, neu_ref_id: input.neuRefId, faellig_am: input.faelligAm, angelegt_von: input.angelegtVon,
  }).select('id').single();
  if (error) throw new Error(error.message);
  return (data as { id: string }).id;
}

export async function legeEigentuemerOrdnerAn(eigentuemerId: string, objektId: string): Promise<void> {
  const { error } = await supabase.functions.invoke('gdrive', { body: { action: 'eigentuemer-ordner-anlegen', eigentuemerId, objektId } });
  if (error) throw new Error(error.message);
}
